import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

// Types
interface Coordinates {
  latitude: number;
  longitude: number;
}

interface LocationCache {
  coordinates: Coordinates;
  timestamp: number;
}

interface LocationState {
  // User's actual GPS location
  userLocation: Coordinates | null;
  // Location used for search (either user location or custom)
  searchLocation: Coordinates | null;
  // Loading states
  isLoadingUserLocation: boolean;
  isLoadingSearchLocation: boolean;
  // Error states
  userLocationError: string | null;
  searchLocationError: string | null;
  // Permission state
  hasLocationPermission: boolean | null; // null = unknown, true = granted, false = denied
  // Mode
  isCustomSearchMode: boolean;
  // Cache info
  lastUserLocationUpdate: number | null;
}

interface LocationContextValue extends LocationState {
  // Actions
  requestUserLocation: (forceRefresh?: boolean) => Promise<Coordinates | null>;
  setCustomSearchLocation: (lat: number, lon: number) => void;
  clearCustomSearchLocation: () => void;
  calculateDistance: (to: Coordinates) => number | null;
  isLocationStale: () => boolean;
}

// Constants
const LOCATION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const LOCATION_TIMEOUT = 15000; // 15 seconds
const LOCATION_REFRESH_TIMEOUT = 8000; // 8 seconds for refresh
const PROXIMITY_THRESHOLD = 10; // metres - if user moved less than this, consider it the same location

// Helper functions
const calculateHaversineDistance = (from: Coordinates, to: Coordinates): number => {
  const R = 6371e3; // Earth radius in metres
  const φ1 = from.latitude * Math.PI / 180;
  const φ2 = to.latitude * Math.PI / 180;
  const Δφ = (to.latitude - from.latitude) * Math.PI / 180;
  const Δλ = (to.longitude - from.longitude) * Math.PI / 180;
  
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return R * c;
};

// Create context
const LocationContext = createContext<LocationContextValue | null>(null);

// Provider component
export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // State
  const [state, setState] = useState<LocationState>(() => {
    // Initialise from localStorage
    const storedPermission = localStorage.getItem('locationPermissionGranted');
    const storedLat = localStorage.getItem('userLatitude');
    const storedLon = localStorage.getItem('userLongitude');
    const searchMode = localStorage.getItem('searchMode');
    const searchAreaParams = localStorage.getItem('searchAreaParams');
    
    let userLocation: Coordinates | null = null;
    let searchLocation: Coordinates | null = null;
    let hasPermission: boolean | null = null;
    
    if (storedPermission === 'true' && storedLat && storedLon) {
      hasPermission = true;
      userLocation = {
        latitude: parseFloat(storedLat),
        longitude: parseFloat(storedLon)
      };
    } else if (storedPermission === 'false') {
      hasPermission = false;
    }
    
    // Determine search location
    if (searchMode === 'custom' && searchAreaParams) {
      try {
        const params = JSON.parse(searchAreaParams);
        searchLocation = {
          latitude: params.latitude,
          longitude: params.longitude
        };
      } catch (e) {
        console.error('Error parsing search area params:', e);
      }
    } else {
      searchLocation = userLocation;
    }
    
    return {
      userLocation,
      searchLocation,
      isLoadingUserLocation: false,
      isLoadingSearchLocation: false,
      userLocationError: null,
      searchLocationError: null,
      hasLocationPermission: hasPermission,
      isCustomSearchMode: searchMode === 'custom',
      lastUserLocationUpdate: null
    };
  });
  
  // Refs for managing async operations
  const locationRequestInProgress = useRef(false);
  const locationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cachedLocationRef = useRef<LocationCache | null>(null);
  
  // Load cached location on mount
  useEffect(() => {
    const cachedData = localStorage.getItem('locationCache');
    if (cachedData) {
      try {
        cachedLocationRef.current = JSON.parse(cachedData);
      } catch (e) {
        console.error('Error parsing location cache:', e);
      }
    }
  }, []);
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (locationTimeoutRef.current) {
        clearTimeout(locationTimeoutRef.current);
      }
    };
  }, []);
  
  // Request user location
  const requestUserLocation = useCallback(async (forceRefresh: boolean = false): Promise<Coordinates | null> => {
    
    // Check if request already in progress
    if (locationRequestInProgress.current) {
      //console.log('Location request already in progress, waiting...');
      // Wait for existing request to complete
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!locationRequestInProgress.current) {
            clearInterval(checkInterval);
            resolve(state.userLocation);
          }
        }, 100);
      });
    }
    
    // Check cache if not forcing refresh
    if (!forceRefresh && cachedLocationRef.current) {
      const now = Date.now();
      const cacheAge = now - cachedLocationRef.current.timestamp;
      
      if (cacheAge < LOCATION_CACHE_DURATION) {
        //console.log('Cache is fresh (age:', Math.round(cacheAge / 1000), 'seconds), checking if location changed...');
        
        // We need to check the actual current position against the cached position
        // Set up a promise to get the current position
        return new Promise<Coordinates | null>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const currentCoordinates: Coordinates = {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              };
              
              const distance = calculateHaversineDistance(
                cachedLocationRef.current!.coordinates,
                currentCoordinates
              );
              
              //console.log('Distance from cached location:', distance.toFixed(1), 'm');
              
              if (distance < PROXIMITY_THRESHOLD) {
                //console.log('User hasn\'t moved significantly, using cached location');
                // Use cached location
                resolve(cachedLocationRef.current!.coordinates);
              } else {
                // console.log('User has moved significantly, updating location');
                // Update with new location
                const cache: LocationCache = {
                  coordinates: currentCoordinates,
                  timestamp: Date.now()
                };
                cachedLocationRef.current = cache;
                localStorage.setItem('locationCache', JSON.stringify(cache));
                localStorage.setItem('userLatitude', currentCoordinates.latitude.toString());
                localStorage.setItem('userLongitude', currentCoordinates.longitude.toString());
                
                setState(prev => ({
                  ...prev,
                  userLocation: currentCoordinates,
                  searchLocation: prev.isCustomSearchMode ? prev.searchLocation : currentCoordinates,
                  lastUserLocationUpdate: cache.timestamp
                }));
                
                resolve(currentCoordinates);
              }
            },
            (error) => {
              console.error('Error checking current position:', error);
              // Fall back to cached location on error
              resolve(cachedLocationRef.current!.coordinates);
            },
            {
              enableHighAccuracy: true,
              timeout: 5000, // Quick timeout for cache check
              maximumAge: 0
            }
          );
        });
      }
    }
    
    // Check if geolocation is available
    if (!navigator.geolocation) {
      setState(prev => ({
        ...prev,
        userLocationError: 'Geolocation is not supported by your browser',
        hasLocationPermission: false
      }));
      return null;
    }
    
    // Set loading state and flag
    locationRequestInProgress.current = true;
    setState(prev => ({
      ...prev,
      isLoadingUserLocation: true,
      userLocationError: null
    }));
    
    return new Promise<Coordinates | null>((resolve) => {
      // Set timeout
      const timeout = forceRefresh ? LOCATION_REFRESH_TIMEOUT : LOCATION_TIMEOUT;
      locationTimeoutRef.current = setTimeout(() => {
        locationRequestInProgress.current = false;
        setState(prev => ({
          ...prev,
          isLoadingUserLocation: false,
          userLocationError: 'Location request timed out. Please try again.'
        }));
        resolve(null);
      }, timeout);
      
      // Request location
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Clear timeout
          if (locationTimeoutRef.current) {
            clearTimeout(locationTimeoutRef.current);
          }
          
          const coordinates: Coordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          
          // Cache the location
          const cache: LocationCache = {
            coordinates,
            timestamp: Date.now()
          };
          cachedLocationRef.current = cache;
          localStorage.setItem('locationCache', JSON.stringify(cache));
          
          // Store in localStorage for backwards compatibility
          localStorage.setItem('userLatitude', coordinates.latitude.toString());
          localStorage.setItem('userLongitude', coordinates.longitude.toString());
          localStorage.setItem('locationPermissionGranted', 'true');
          
          // Update state
          setState(prev => ({
            ...prev,
            userLocation: coordinates,
            searchLocation: prev.isCustomSearchMode ? prev.searchLocation : coordinates,
            isLoadingUserLocation: false,
            userLocationError: null,
            hasLocationPermission: true,
            lastUserLocationUpdate: cache.timestamp
          }));
          
          locationRequestInProgress.current = false;
          resolve(coordinates);
        },
        (error) => {
          // Clear timeout
          if (locationTimeoutRef.current) {
            clearTimeout(locationTimeoutRef.current);
          }
          
          let errorMessage = 'Unable to retrieve your location.';
          if (error.code === 1) {
            errorMessage = 'Location permission denied.';
            localStorage.setItem('locationPermissionGranted', 'false');
          }
          
          setState(prev => ({
            ...prev,
            isLoadingUserLocation: false,
            userLocationError: errorMessage,
            hasLocationPermission: error.code === 1 ? false : prev.hasLocationPermission
          }));
          
          locationRequestInProgress.current = false;
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: timeout - 1000, // Slightly less than our timeout
          maximumAge: 0
        }
      );
    });
  }, [state.userLocation, state.isCustomSearchMode]);
  
  // Set custom search location
  const setCustomSearchLocation = useCallback((lat: number, lon: number) => {
    const coordinates: Coordinates = {
      latitude: lat,
      longitude: lon
    };
    
    // Store in localStorage
    localStorage.setItem('searchMode', 'custom');
    localStorage.setItem('searchAreaParams', JSON.stringify(coordinates));
    
    setState(prev => ({
      ...prev,
      searchLocation: coordinates,
      isCustomSearchMode: true
    }));
  }, []);
  
  // Clear custom search location
  const clearCustomSearchLocation = useCallback(() => {
    localStorage.removeItem('searchMode');
    localStorage.removeItem('searchAreaParams');
    
    setState(prev => ({
      ...prev,
      searchLocation: prev.userLocation,
      isCustomSearchMode: false
    }));
  }, []);
  
  // Calculate distance from user location to given coordinates
  const calculateDistance = useCallback((to: Coordinates): number | null => {
    if (!state.userLocation) return null;
    return calculateHaversineDistance(state.userLocation, to);
  }, [state.userLocation]);
  
  // Check if location is stale
  const isLocationStale = useCallback((): boolean => {
    if (!state.lastUserLocationUpdate) return true;
    const age = Date.now() - state.lastUserLocationUpdate;
    return age > LOCATION_CACHE_DURATION;
  }, [state.lastUserLocationUpdate]);
  
  // Context value
  const value: LocationContextValue = {
    ...state,
    requestUserLocation,
    setCustomSearchLocation,
    clearCustomSearchLocation,
    calculateDistance,
    isLocationStale
  };
  
  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

// Custom hook
export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

// Export types
export type { Coordinates, LocationState, LocationContextValue };